import { useTranslation } from 'react-i18next'
import { Tooltip } from 'react-tippy'
import { useLocation } from 'react-router-dom'

import { ArrowsLeftRightIcon, TimerIcon, UserCircleIcon } from '@phosphor-icons/react'
import { useTheme } from 'styled-components'

import {
  Alert,
  AllowListIcon,
  Checkbox,
  GiftReturnChip,
  HasCommentIcon,
  InstantChip,
  Link,
  NoInstantChip,
  ReturnProtectionChip,
  SequenceNumber,
  Text,
  TransactionAmount,
  TransactionTypeChip,
} from '@reveni-io/reveni-ui'

import UpdatedTimeText from '@/common/components/UpdatedTimeText/UpdatedTimeText'
import { userPermissions } from '@/common/constants'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'
import { statuses } from '@/returns/constants'
import routes from '@/Router/routes.constants'

import OrderItemDate from '../OrderItemDate/OrderItemDate'
import SelectBadgeStatus from '../SelectBadgeStatus/SelectBadgeStatus'
import DeliveryMethod from './DeliveryMethod/DeliveryMethod'
import StoreName from './StoreName/StoreName'
import { orderStatuses } from './constants'
import * as Styles from './styles'

const OrderListItem = ({
  info,
  onChangeStatus,
  href,
  currency,
  selected,
  onCheckOrder,
  showCheckBox = true,
  storeName,
}) => {
  const theme = useTheme()

  const { t, i18n } = useTranslation()
  const { hasPermission } = useUserPermissions()
  const location = useLocation()

  const handleChangeStatus = status => {
    onChangeStatus({
      orderId: info?.id,
      orderNumber: info?.order?.number,
      status,
      issueRefundAtStatus: info?.issue_refund_at_status,
      allowsIssueRefund: info?.allows_issue_refund,
      issueExchangeAtStatus: info?.issue_exchange_at_status,
      allowsExchangeOrderProcessing: info?.allows_exchange_order_processing,
    })
  }

  const isCheckboxDisabled = () => {
    return !info?.available_status?.length
  }

  return (
    <Styles.MainContainer to={href} state={{ from: location }} selected={selected}>
      <Styles.Header>
        <div>
          <Styles.CheckboxContainer onClick={e => e.stopPropagation()}>
            {showCheckBox && <Checkbox checked={selected} onChange={onCheckOrder} disabled={isCheckboxDisabled()} />}
          </Styles.CheckboxContainer>
          <Text
            type="titleM"
            color={info?.status === statuses.cancelled ? theme.colors.text.subtle : theme.colors.text.default}
            customStyles={{
              marginLeft: `-${theme.spacing.sp8}`,
            }}
          >
            {info?.order?.name}
          </Text>
          <SequenceNumber position={info?.sequence_number} total={info?.returns_in_order} />
          <StoreName status={info?.status} storeName={storeName} />
          {info?.order?.from_exchange_order && (
            <Tooltip
              title={t('El pedido {{orderNumber}} proviene de un cambio sobre el pedido {{fromExchangeOrder}}', {
                orderNumber: info?.order?.name,
                fromExchangeOrder: info?.order?.from_exchange_order.name,
              })}
            >
              <Link
                href={`${routes.transactionDetailPage.replace(':id', info?.order.from_exchange_order?.return_id)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ArrowsLeftRightIcon data-testid="fromExchangeOrder" />
                {info?.order.from_exchange_order?.name}
              </Link>
            </Tooltip>
          )}
          <TransactionTypeChip status={info?.status} type={info?.transaction_type} />
          {info?.is_instant ? <InstantChip status={info?.status} /> : <NoInstantChip status={info?.status} />}
          {info?.is_covered_by_return_protection && <ReturnProtectionChip />}
          {info?.is_gift_return && <GiftReturnChip />}
          {info?.allow_list && <AllowListIcon />}
          {info?.has_comments && <HasCommentIcon />}
        </div>
        <div>
          <Styles.StatusContainer>
            {info?.days_to_deadline > 0 && (
              <Styles.Deadline days={info.days_to_deadline}>
                <TimerIcon size={16} weight="light" /> {info?.days_to_deadline} {t('días')}
              </Styles.Deadline>
            )}
            <SelectBadgeStatus
              statusOptions={
                hasPermission(userPermissions.editReturnStatus, info?.store_id) ? info?.available_status : []
              }
              selected={info?.status}
              onChange={handleChangeStatus}
              fullWidth
              height="34px"
            />
          </Styles.StatusContainer>
        </div>
      </Styles.Header>
      <Styles.DetailContainer>
        {info?.has_alerts && <Alert type="warning">{t('Alertas pendientes')}</Alert>}
        <Styles.DetailFirstLine status={info.status}>
          <Styles.UserInfo>
            <UserCircleIcon size={24} weight="light" />
            <Text type="bodyMRegular">
              {info?.is_gift_return
                ? `${info?.gift_recipient?.first_name} ${info?.gift_recipient?.last_name}`
                : info?.customer?.name}
            </Text>
            ·<Text type="bodyMRegular">{info?.is_gift_return ? info?.gift_recipient?.email : info?.order?.email}</Text>
          </Styles.UserInfo>
          {info.status !== orderStatuses.cancelled && (
            <TransactionAmount
              paymentAmount={info?.payment_amount}
              summaryTotalAmount={info?.summary_total_amount}
              currency={currency}
              paymentStatus={info?.payment_status}
              refundMethod={info?.refund_method}
              refundStatus={info?.refund_status}
            />
          )}
        </Styles.DetailFirstLine>
        <Styles.DetailSecondLine status={info.status}>
          <Styles.UpdatedAndDate>
            <OrderItemDate language={i18n.language} completedAt={info?.completed_at} status={info?.status} />
            <span>|</span>
            <UpdatedTimeText modified={info?.modified} />
          </Styles.UpdatedAndDate>
          <DeliveryMethod deliveryMethod={info?.delivery_method?.type} />
        </Styles.DetailSecondLine>
      </Styles.DetailContainer>
    </Styles.MainContainer>
  )
}

export default OrderListItem
